import React, { useState, useMemo } from 'react';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { Label } from '@components/atoms/Label';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import {
  PlumbingRun,
  PlumbingRunResult,
  PlumbingCalculatorResults,
  PipeMaterial,
  PipeSize,
  FixtureType,
  OnAddToMaterialCallback,
  OnAddAllMaterialsCallback,
  MaterialFromCalculator,
} from '../../types/calculator.types';
import {
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Ruler,
} from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';
import { PSI, WSFU, GPM } from '@utils/calculatorUnits';

// --- INDUSTRY STANDARDS: EQUIVALENT LENGTHS (FEET OF PIPE) ---
interface PipeDataEntry {
  roughness: number;
  sizes: Record<string, number>;
  equiv90: number;
  equiv45: number;
  equivTeeBranch: number;
  equivTeeLine: number;
}

const pipeData: Record<PipeMaterial, PipeDataEntry> = {
  PEX: {
    roughness: 150,
    sizes: { '1/2"': 0.475, '3/4"': 0.671, '1"': 0.862 },
    equiv90: 2.0,
    equiv45: 1.0,
    equivTeeBranch: 3.0,
    equivTeeLine: 0.5,
  },
  Copper_L: {
    roughness: 140,
    sizes: { '1/2"': 0.545, '3/4"': 0.785, '1"': 1.025 },
    equiv90: 1.6,
    equiv45: 0.8,
    equivTeeBranch: 2.4,
    equivTeeLine: 0.4,
  },
};

interface FixtureUnitData {
  wsfu: number;
  dfu: number;
  gpm: number;
}

const fixtureUnits: Record<FixtureType, FixtureUnitData> = {
  'Toilet (HE)': { wsfu: 1.1, dfu: 3, gpm: 2.2 },
  Shower: { wsfu: 2.0, dfu: 2, gpm: 2.5 },
  'Kitchen Sink': { wsfu: 1.5, dfu: 2, gpm: 2.2 },
  Lavatory: { wsfu: 0.7, dfu: 1, gpm: 1.5 },
  'Garden Tub': { wsfu: 4.0, dfu: 2, gpm: 5.0 },
};

interface PlumbingCalculatorProps {
  onAddToMaterial?: OnAddToMaterialCallback;
  onAddAllMaterials?: OnAddAllMaterialsCallback;
}

const PlumbingCalculator: React.FC<PlumbingCalculatorProps> = ({
  onAddToMaterial,
  onAddAllMaterials,
}) => {
  const tCalc = useTranslation('calculators');

  // Sprint 30, story-151 review Critical #4. Previously module-level hardcoded English
  // `SelectOption[]` arrays, invisible to the guard's object-literal-only check. `value` fields
  // are unchanged (they are `PipeMaterial`/`FixtureType` literal union members read elsewhere as
  // data keys); only the rendered `label` is translated.
  const materialOptions: SelectOption[] = [
    { value: 'PEX', label: 'PEX' },
    { value: 'Copper_L', label: tCalc.plumbing.materialCopperL },
  ];

  const sizeOptions: SelectOption[] = [
    { value: '1/2"', label: '1/2"' },
    { value: '3/4"', label: '3/4"' },
    { value: '1"', label: '1"' },
  ];

  const fixtureOptions: SelectOption[] = [
    { value: 'Toilet (HE)', label: tCalc.plumbing.fixtureToiletHe },
    { value: 'Shower', label: tCalc.plumbing.fixtureShower },
    { value: 'Kitchen Sink', label: tCalc.plumbing.fixtureKitchenSink },
    { value: 'Lavatory', label: tCalc.plumbing.fixtureLavatory },
    { value: 'Garden Tub', label: tCalc.plumbing.fixtureGardenTub },
  ];

  const [runs, setRuns] = useState<PlumbingRun[]>([
    {
      id: 1,
      label: tCalc.plumbing.mainTrunk,
      material: 'PEX',
      size: '3/4"',
      length: '40',
      fixture: 'Kitchen Sink',
      count: '1',
      elbows90: '2',
      elbows45: '0',
      teesLine: '3',
      teesBranch: '1',
    },
  ]);

  const [sourcePressure, setSourcePressure] = useState<string>('60');
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [newRun, setNewRun] = useState<Omit<PlumbingRun, 'id'>>({
    label: '',
    material: 'PEX',
    size: '1/2"',
    length: '20',
    fixture: 'Lavatory',
    count: '1',
    elbows90: '2',
    elbows45: '0',
    teesLine: '0',
    teesBranch: '1',
  });

  // Update a run
  const updateRun = (
    id: number,
    field: keyof PlumbingRun,
    value: string | number,
  ) => {
    setRuns(
      runs.map((run) => (run.id === id ? { ...run, [field]: value } : run)),
    );
  };

  // Delete a run
  const deleteRun = (id: number) => {
    if (runs.length > 1) {
      setRuns(runs.filter((r) => r.id !== id));
    }
  };

  // Add a new run
  const addRun = () => {
    if (!newRun.label.trim()) return;
    setRuns([...runs, { ...newRun, id: Date.now() }]);
    setNewRun({
      label: '',
      material: 'PEX',
      size: '1/2"',
      length: '20',
      fixture: 'Lavatory',
      count: '1',
      elbows90: '2',
      elbows45: '0',
      teesLine: '0',
      teesBranch: '1',
    });
    setShowAddForm(false);
  };

  // Main calculations
  const results: PlumbingCalculatorResults = useMemo(() => {
    const sourcePressureNum = parseFloat(sourcePressure) || 60;

    const runResults: PlumbingRunResult[] = runs.map((run) => {
      const unit = fixtureUnits[run.fixture];
      const pipe = pipeData[run.material];

      const lengthNum = parseFloat(run.length) || 0;
      const countNum = parseInt(run.count, 10) || 1;
      const elbows90Num = parseInt(run.elbows90, 10) || 0;
      const elbows45Num = parseInt(run.elbows45, 10) || 0;
      const teesLineNum = parseInt(run.teesLine, 10) || 0;
      const teesBranchNum = parseInt(run.teesBranch, 10) || 0;

      // EQUIVALENT LENGTH CALCULATION (Pipe + Bends + Intersections)
      const fittingsLength =
        elbows90Num * pipe.equiv90 +
        elbows45Num * pipe.equiv45 +
        teesBranchNum * pipe.equivTeeBranch +
        teesLineNum * pipe.equivTeeLine;

      const totalEffectiveLength = lengthNum + fittingsLength;
      const totalWSFU = unit.wsfu * countNum;
      const gpm = totalWSFU * 0.42 + 1.5;
      const idia = pipe.sizes[run.size];

      // Hazen-Williams Friction Loss
      const frictionLoss =
        (4.52 * totalEffectiveLength * Math.pow(gpm, 1.85)) /
        (Math.pow(pipe.roughness, 1.85) * Math.pow(idia, 4.87));
      const endPressure = sourcePressureNum - frictionLoss;

      return {
        ...run,
        wsfu: totalWSFU,
        gpm,
        endPressure,
        totalEffectiveLength,
        isSafe: endPressure >= 30,
        failReason: endPressure < 30 ? tCalc.plumbing.criticalPressureDrop : null,
      };
    });

    // BOM Aggregation
    const pipeMap: Record<string, number> = {};
    const fixturesMap: Record<string, number> = {};
    const hardware = { elbows90: 0, elbows45: 0, tees: 0 };

    runResults.forEach((r) => {
      const lengthVal = parseFloat(r.length) || 0;
      const countVal = parseInt(r.count, 10) || 1;
      const elbows90Val = parseInt(r.elbows90, 10) || 0;
      const elbows45Val = parseInt(r.elbows45, 10) || 0;
      const teesLineVal = parseInt(r.teesLine, 10) || 0;
      const teesBranchVal = parseInt(r.teesBranch, 10) || 0;

      const key = `${r.size} ${r.material}`;
      pipeMap[key] = (pipeMap[key] || 0) + lengthVal;
      fixturesMap[r.fixture] = (fixturesMap[r.fixture] || 0) + countVal;
      hardware.elbows90 += elbows90Val;
      hardware.elbows45 += elbows45Val;
      hardware.tees += teesBranchVal + teesLineVal;
    });

    return {
      runs: runResults,
      bom: {
        pipe: Object.entries(pipeMap).map(([name, feet]) => ({ name, feet })),
        fixtures: Object.entries(fixturesMap).map(([name, count]) => ({
          name,
          count,
        })),
        hardware,
      },
    };
  }, [runs, sourcePressure, tCalc]);

  // Convert BOM to materials for adding
  const getMaterialsFromBOM = (): MaterialFromCalculator[] => {
    const materials: MaterialFromCalculator[] = [];

    results.bom.pipe.forEach((p) => {
      materials.push({
        amount: p.feet,
        amountType: 'linearFeet',
        amountSystem: 'imperial',
        suggestedName: `${p.name} ${tCalc.materials.pipeIndividual}`,
        resultLabel: `${tCalc.materials.pipe}: ${p.name}`,
      });
    });

    materials.push({
      amount: results.bom.hardware.elbows90,
      amountType: 'units',
      amountSystem: 'imperial',
      suggestedName: tCalc.plumbing.elbows90,
      resultLabel: tCalc.plumbing.elbows90,
    });

    materials.push({
      amount: results.bom.hardware.elbows45,
      amountType: 'units',
      amountSystem: 'imperial',
      suggestedName: tCalc.plumbing.elbows45,
      resultLabel: tCalc.plumbing.elbows45,
    });

    materials.push({
      amount: results.bom.hardware.tees,
      amountType: 'units',
      amountSystem: 'imperial',
      suggestedName: tCalc.plumbing.teeFittings,
      resultLabel: tCalc.plumbing.teeFittings,
    });

    return materials.filter((m) => m.amount > 0);
  };

  const allRunsSafe = results.runs.every((r) => r.isSafe);

  return (
    <div>
      <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-6">
        {tCalc.names.plumbing}
      </h3>
      <p className="text-sm text-center text-gray-500 dark:text-gray-400 mb-6">
        {tCalc.plumbing.subtitle}
      </p>

      {/* Global Settings */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label label={tCalc.plumbing.sourcePressurePsi} htmlFor="sourcePressure" />
            <Input
              id="sourcePressure"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 60`}
              value={sourcePressure}
              onChange={(e) => setSourcePressure(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="flex items-end">
            <div
              className={`w-full p-3 rounded-lg text-center font-medium ${
                allRunsSafe
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                  : 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400'
              }`}>
              {allRunsSafe ? (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" /> {tCalc.plumbing.allRunsAdequatePressure}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> {tCalc.plumbing.someRunsLowPressure}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pipe Runs List */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white">
            {tCalc.plumbing.pipeRuns} ({runs.length})
          </h4>
          <Button
            variant="secondary"
            size="md"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowAddForm(!showAddForm)}>
            {tCalc.actions.addRun}
          </Button>
        </div>

        {/* Add New Run Form */}
        {showAddForm && (
          <div className="mb-4 p-4 border-2 border-dashed border-indigo-300 dark:border-indigo-600 rounded-lg bg-indigo-50/50 dark:bg-indigo-900/20">
            <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {tCalc.plumbing.newPipeRun}
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <Label label={tCalc.plumbing.runLabel} htmlFor="newLabel" />
                <Input
                  id="newLabel"
                  type="text"
                  placeholder={tCalc.plumbing.exampleRunLabel}
                  value={newRun.label}
                  onChange={(e) =>
                    setNewRun({ ...newRun, label: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label label={tCalc.plumbing.pipeMaterial} htmlFor="newMaterial" />
                <div className="mt-1">
                  <ReactSelect
                    options={materialOptions}
                    value={materialOptions.find(
                      (opt) => opt.value === newRun.material,
                    )}
                    onChange={(option) => {
                      if (option && 'value' in option) {
                        setNewRun({
                          ...newRun,
                          material: option.value as PipeMaterial,
                        });
                      }
                    }}
                    isClearable={false}
                    isSearchable={false}
                  />
                </div>
              </div>
              <div>
                <Label label={tCalc.plumbing.pipeSize} htmlFor="newSize" />
                <div className="mt-1">
                  <ReactSelect
                    options={sizeOptions}
                    value={sizeOptions.find((opt) => opt.value === newRun.size)}
                    onChange={(option) => {
                      if (option && 'value' in option) {
                        setNewRun({
                          ...newRun,
                          size: option.value as PipeSize,
                        });
                      }
                    }}
                    isClearable={false}
                    isSearchable={false}
                  />
                </div>
              </div>
              <div>
                <Label label={tCalc.plumbing.fixtureType} htmlFor="newFixture" />
                <div className="mt-1">
                  <ReactSelect
                    options={fixtureOptions}
                    value={fixtureOptions.find(
                      (opt) => opt.value === newRun.fixture,
                    )}
                    onChange={(option) => {
                      if (option && 'value' in option) {
                        setNewRun({
                          ...newRun,
                          fixture: option.value as FixtureType,
                        });
                      }
                    }}
                    isClearable={false}
                    isSearchable={false}
                  />
                </div>
              </div>
              <div>
                <Label label={tCalc.dimensions.lengthFt} htmlFor="newLength" />
                <Input
                  id="newLength"
                  type="number"
                  placeholder={`${tCalc.actions.examplePrefix}, 20`}
                  value={newRun.length}
                  onChange={(e) =>
                    setNewRun({ ...newRun, length: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label label={tCalc.plumbing.fixtureCount} htmlFor="newCount" />
                <Input
                  id="newCount"
                  type="number"
                  placeholder={`${tCalc.actions.examplePrefix}, 1`}
                  value={newRun.count}
                  onChange={(e) =>
                    setNewRun({ ...newRun, count: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label label={tCalc.plumbing.elbows90} htmlFor="newElbows90" />
                <Input
                  id="newElbows90"
                  type="number"
                  placeholder={`${tCalc.actions.examplePrefix}, 2`}
                  value={newRun.elbows90}
                  onChange={(e) =>
                    setNewRun({ ...newRun, elbows90: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label label={tCalc.plumbing.elbows45} htmlFor="newElbows45" />
                <Input
                  id="newElbows45"
                  type="number"
                  placeholder={`${tCalc.actions.examplePrefix}, 0`}
                  value={newRun.elbows45}
                  onChange={(e) =>
                    setNewRun({ ...newRun, elbows45: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label label={tCalc.plumbing.teesLineBranch} htmlFor="newTees" />
                <div className="flex gap-2 mt-1">
                  <Input
                    type="number"
                    placeholder={tCalc.plumbing.teeLinePlaceholder}
                    value={newRun.teesLine}
                    onChange={(e) =>
                      setNewRun({ ...newRun, teesLine: e.target.value })
                    }
                  />
                  <Input
                    type="number"
                    placeholder={tCalc.plumbing.teeBranchPlaceholder}
                    value={newRun.teesBranch}
                    onChange={(e) =>
                      setNewRun({ ...newRun, teesBranch: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="primary" size="md" onClick={addRun}>
                {tCalc.actions.addRun}
              </Button>
              <Button
                variant="tertiary"
                size="md"
                onClick={() => setShowAddForm(false)}>
                {tCalc.actions.cancel}
              </Button>
            </div>
          </div>
        )}

        {/* Run Cards */}
        <div className="space-y-4">
          {results.runs.map((run) => (
            <div
              key={run.id}
              className={`p-4 rounded-lg border-2 transition-all ${
                !run.isSafe
                  ? 'border-rose-400 bg-rose-50/50 dark:bg-rose-900/20 dark:border-rose-600'
                  : 'border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700'
              }`}>
              {/* Run Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    value={run.label}
                    onChange={(e) => updateRun(run.id, 'label', e.target.value)}
                    className="font-semibold border-none bg-transparent p-0 text-base w-40"
                  />
                  <span className="text-xs font-medium bg-blue-600 text-white px-2 py-0.5 rounded">
                    {tCalc.plumbing.supply}
                  </span>
                  <span className="text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">
                    {run.material}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button"
                    onClick={() => deleteRun(run.id)}
                    disabled={runs.length <= 1}
                    className="text-gray-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title={tCalc.actions.deleteRun}>
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Run Details Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 items-end">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {tCalc.plumbing.size}
                  </p>
                  <div className="[&_.react-select\_\_control]:!min-h-[38px] [&_.react-select\_\_control]:!h-[38px]">
                    <ReactSelect
                      options={sizeOptions}
                      value={sizeOptions.find((opt) => opt.value === run.size)}
                      onChange={(option) => {
                        if (option && 'value' in option) {
                          updateRun(run.id, 'size', option.value as string);
                        }
                      }}
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {tCalc.dimensions.lengthFt}
                  </p>
                  <Input
                    type="number"
                    value={run.length}
                    onChange={(e) =>
                      updateRun(run.id, 'length', e.target.value)
                    }
                    className="h-[38px]"
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {tCalc.plumbing.fixture}
                  </p>
                  <div className="[&_.react-select\_\_control]:!min-h-[38px] [&_.react-select\_\_control]:!h-[38px]">
                    <ReactSelect
                      options={fixtureOptions}
                      value={fixtureOptions.find(
                        (opt) => opt.value === run.fixture,
                      )}
                      onChange={(option) => {
                        if (option && 'value' in option) {
                          updateRun(run.id, 'fixture', option.value as string);
                        }
                      }}
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {tCalc.plumbing.elbows9045}
                  </p>
                  <div className="flex gap-1">
                    <Input
                      type="number"
                      value={run.elbows90}
                      onChange={(e) =>
                        updateRun(run.id, 'elbows90', e.target.value)
                      }
                      className="h-[38px]"
                    />
                    <Input
                      type="number"
                      value={run.elbows45}
                      onChange={(e) =>
                        updateRun(run.id, 'elbows45', e.target.value)
                      }
                      className="h-[38px]"
                    />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {tCalc.plumbing.teesLineBranch}
                  </p>
                  <div className="flex gap-1">
                    <Input
                      type="number"
                      value={run.teesLine}
                      onChange={(e) =>
                        updateRun(run.id, 'teesLine', e.target.value)
                      }
                      className="h-[38px]"
                    />
                    <Input
                      type="number"
                      value={run.teesBranch}
                      onChange={(e) =>
                        updateRun(run.id, 'teesBranch', e.target.value)
                      }
                      className="h-[38px]"
                    />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {tCalc.plumbing.endPressure}
                  </p>
                  <div
                    className={`h-[38px] flex items-center justify-center rounded-lg font-bold ${
                      run.isSafe
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
                    }`}>
                    {run.endPressure.toFixed(0)} {PSI}
                  </div>
                </div>
              </div>

              {/* Calculation Results */}
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
                  <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                    <Ruler className="w-4 h-4 text-blue-500" />
                    <span>
                      {tCalc.plumbing.effectiveLength}{' '}
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {run.totalEffectiveLength.toFixed(1)} ft
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-600 dark:text-gray-400">
                      {WSFU}:{' '}
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {run.wsfu.toFixed(1)}
                      </span>
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      {GPM}:{' '}
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {run.gpm.toFixed(1)}
                      </span>
                    </span>
                  </div>
                  {!run.isSafe && (
                    <div className="text-rose-600 dark:text-rose-400 font-semibold flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" /> {run.failReason}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* BOM Summary */}
      {results.bom.pipe.length > 0 && (
        <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border-t-4 border-indigo-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
            {tCalc.actions.billOfMaterialsSummary}
          </h4>

          {/* Pipe Inventory */}
          <div className="mb-4">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {tCalc.plumbing.pipeInventory}
            </h5>
            <div className="space-y-2">
              {results.bom.pipe.map((p, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {tCalc.materials.pipe} {p.name}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                      {p.feet} ft
                    </span>
                    {onAddToMaterial && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        leftIcon={<Plus className="w-3 h-3" />}
                        onClick={() =>
                          onAddToMaterial({
                            amount: p.feet,
                            amountType: 'linearFeet',
                            amountSystem: 'imperial',
                            suggestedName: `${p.name} ${tCalc.materials.pipeIndividual}`,
                            resultLabel: `${tCalc.materials.pipe}: ${p.name}`,
                          })
                        }>
                        {tCalc.actions.add}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fitting Inventory */}
          <div className="mb-4">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {tCalc.plumbing.fittingInventory}
            </h5>
            <div className="space-y-2">
              {results.bom.hardware.elbows90 > 0 && (
                <div className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {tCalc.plumbing.elbows90}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                      {results.bom.hardware.elbows90}{' '}
                      {results.bom.hardware.elbows90 === 1
                        ? tCalc.plumbing.fittingUnitOne
                        : tCalc.plumbing.fittingUnitOther}
                    </span>
                    {onAddToMaterial && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        leftIcon={<Plus className="w-3 h-3" />}
                        onClick={() =>
                          onAddToMaterial({
                            amount: results.bom.hardware.elbows90,
                            amountType: 'units',
                            amountSystem: 'imperial',
                            suggestedName: tCalc.plumbing.elbows90,
                            resultLabel: tCalc.plumbing.elbows90,
                          })
                        }>
                        {tCalc.actions.add}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {results.bom.hardware.elbows45 > 0 && (
                <div className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {tCalc.plumbing.elbows45}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                      {results.bom.hardware.elbows45}{' '}
                      {results.bom.hardware.elbows45 === 1
                        ? tCalc.plumbing.fittingUnitOne
                        : tCalc.plumbing.fittingUnitOther}
                    </span>
                    {onAddToMaterial && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        leftIcon={<Plus className="w-3 h-3" />}
                        onClick={() =>
                          onAddToMaterial({
                            amount: results.bom.hardware.elbows45,
                            amountType: 'units',
                            amountSystem: 'imperial',
                            suggestedName: tCalc.plumbing.elbows45,
                            resultLabel: tCalc.plumbing.elbows45,
                          })
                        }>
                        {tCalc.actions.add}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {results.bom.hardware.tees > 0 && (
                <div className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {tCalc.plumbing.teeFittings}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                      {results.bom.hardware.tees}{' '}
                      {results.bom.hardware.tees === 1
                        ? tCalc.plumbing.fittingUnitOne
                        : tCalc.plumbing.fittingUnitOther}
                    </span>
                    {onAddToMaterial && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        leftIcon={<Plus className="w-3 h-3" />}
                        onClick={() =>
                          onAddToMaterial({
                            amount: results.bom.hardware.tees,
                            amountType: 'units',
                            amountSystem: 'imperial',
                            suggestedName: tCalc.plumbing.teeFittings,
                            resultLabel: tCalc.plumbing.teeFittings,
                          })
                        }>
                        {tCalc.actions.add}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Engineering Note */}
          <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <p className="text-xs text-blue-800 dark:text-blue-300 font-medium">
              <strong>{tCalc.actions.note}</strong> {tCalc.plumbing.engineeringNote}
            </p>
          </div>

          {/* Add All Materials Button */}
          {onAddAllMaterials && (
            <div className="mt-4 pt-4 border-t border-indigo-200 dark:border-indigo-700">
              <Button
                type="button"
                variant="primary"
                fullWidth
                size="lg"
                leftIcon={<Plus className="w-5 h-5" />}
                onClick={() => onAddAllMaterials(getMaterialsFromBOM())}>
                {tCalc.actions.addAllMaterials} ({getMaterialsFromBOM().length} {tCalc.actions.items})
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlumbingCalculator;
